/**
 * Entregas do Mercado Livre: o que despachar agora (por prazo), o que está a caminho, entregue hoje e com problema,
 * por modalidade (Instant, Flex, Coleta, Agência/Places, Full). Atualiza sozinha a cada minuto.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';
import { api, brl, dateTime, int, pct } from '../api.ts';
import { ErrorBox, Tag } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { OrderDialog } from './Orders.tsx';

type Mode = 'instant' | 'flex' | 'coleta' | 'agencia' | 'full' | 'outro';
type Bucket = 'to_ship' | 'late' | 'due_soon' | 'in_transit' | 'delivered_today' | 'problem';
interface ModeStats {
  mode: Mode; toShip: number; late: number; dueSoon: number; inTransit: number; deliveredToday: number; problem: number;
  shippedOnTimePct: number | null; deliveredOnTimePct: number | null; avgToShipMin: number | null; avgToDeliverMin: number | null;
}
interface Row {
  shipmentId: string; orderId: string; displayId: string; orders: number; mode: Mode; buyer: string | null; units: number; total: number | null;
  firstTitle: string | null; items: number; createdAt: string; handlingLimit: string | null; estimatedDelivery: string | null;
  shippedAt: string | null; deliveredAt: string | null; status: string | null; substatus: string | null; slaStatus: string | null;
  service: string | null; tracking: string | null; cancelled: boolean; bucket: Bucket | null;
}
interface Page { modes: ModeStats[]; total: number; page: number; pageSize: number; rows: Row[] }

const MODE: Record<Mode, string> = { instant: 'Instant', flex: 'Flex', coleta: 'Coleta', agencia: 'Agência/Places', full: 'Full', outro: 'Outro' };
const BUCKET: Record<Bucket, { label: string; tone: string; note: string }> = {
  late: { label: 'Atrasados', tone: 'err', note: 'passaram do prazo de despacho' },
  due_soon: { label: 'Vencem logo', tone: 'warn', note: 'Instant: 30 min · demais: 2 h' },
  to_ship: { label: 'Para despachar', tone: 'info', note: 'pagos, ainda na loja' },
  in_transit: { label: 'A caminho', tone: 'info', note: 'despachados' },
  problem: { label: 'Com problema', tone: 'err', note: 'ausente, endereço, recusa…' },
  delivered_today: { label: 'Entregues hoje', tone: 'ok', note: '' },
};
const STATUS: Record<string, string> = {
  pending: 'pendente', handling: 'em preparação', ready_to_ship: 'pronto para envio', shipped: 'a caminho', delivered: 'entregue',
  not_delivered: 'não entregue', cancelled: 'cancelado',
};
const SUB: Record<string, string> = {
  ready_to_print: 'etiqueta a imprimir', printed: 'etiqueta impressa', invoice_pending: 'aguardando nota', picked_up: 'coletado',
  ready_for_pickup: 'pronto para coleta', ready_for_dropoff: 'levar à agência', dropped_off: 'entregue na agência', in_hub: 'no centro de distribuição',
  in_transit: 'em trânsito', looking_for_driver: 'procurando entregador', on_route_to_pickup: 'entregador a caminho da loja', picking_up: 'coletando',
  out_for_delivery: 'saiu para entrega', soon_deliver: 'entrega em breve', at_the_door: 'na porta do comprador', receiver_absent: 'comprador ausente',
  bad_address: 'endereço com problema', delivery_failed: 'entrega falhou', refused_delivery: 'entrega recusada', not_localized: 'não localizado',
  changed_address: 'endereço alterado', returning_to_sender: 'voltando para a loja', delayed: 'atrasado', retained: 'retido', stale: 'parado',
  waiting_for_withdrawal: 'aguardando retirada', rider_not_found: 'sem entregador',
};
const ORDER: Bucket[] = ['late', 'due_soon', 'to_ship', 'in_transit', 'problem', 'delivered_today'];

const mins = (m: number | null) => (m == null ? '—' : m < 90 ? `${m} min` : m < 48 * 60 ? `${Math.round(m / 6) / 10} h` : `${Math.round(m / 144) / 10} dias`);
function countdown(iso: string | null) {
  if (!iso) return '—';
  const d = Math.round((Date.parse(iso) - Date.now()) / 60_000);
  const abs = Math.abs(d);
  const txt = abs < 60 ? `${abs} min` : abs < 48 * 60 ? `${Math.floor(abs / 60)} h ${abs % 60 ? `${abs % 60} min` : ''}` : `${Math.round(abs / 1440)} dias`;
  return d < 0 ? `atrasado ${txt}` : `em ${txt}`;
}

export function DeliveriesPage() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const [open, setOpen] = useState<string | null>(null);
  const mode = (params.get('mode') as Mode | null) ?? null;
  const bucket = (params.get('bucket') as Bucket | null) ?? null;
  const page = Number(params.get('page') ?? 1);
  const q = new URLSearchParams({ page: String(page), pageSize: '100', days: params.get('days') ?? '7' });
  if (mode) q.set('mode', mode);
  if (bucket) q.set('bucket', bucket);
  if (params.get('q')) q.set('q', params.get('q')!);
  const data = useQuery({
    queryKey: ['deliveries', q.toString()],
    queryFn: () => api<Page>(`/deliveries?${q}`),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const d = data.data;
  const modes = d?.modes ?? [];
  const sel = mode ? modes.filter((m) => m.mode === mode) : modes;
  const sum = (k: keyof ModeStats) => sel.reduce((t, m) => t + (Number(m[k]) || 0), 0);
  const one = sel.length === 1 ? sel[0]! : null;

  return (
    <>
      <Head title="Entregas">
        <p>Envios do Mercado Livre por modalidade. O prazo de despacho vem do Mercado Livre (SLA); a situação muda sozinha quando o envio anda.</p>
      </Head>
      <div className="stack-y">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="seg" role="group" aria-label="Modalidade">
            <button type="button" aria-pressed={!mode} onClick={() => set({ mode: null, page: null })}>Todas</button>
            {(['instant', 'flex', 'coleta', 'agencia', 'full'] as Mode[]).filter((m) => modes.some((x) => x.mode === m)).map((m) => {
              const s = modes.find((x) => x.mode === m)!;
              return <button key={m} type="button" aria-pressed={mode === m} onClick={() => set({ mode: m, page: null })}>{MODE[m]}{s.late ? <span style={{ color: 'var(--err)' }}> · {s.late}</span> : null}</button>;
            })}
          </div>
          <span style={{ flex: 1 }} />
          <span className="small muted">{data.isFetching ? <><RefreshCw size={12} /> atualizando…</> : `atualizado ${dateTime(new Date(data.dataUpdatedAt || Date.now()).toISOString())}`}</span>
        </div>

        {data.error ? <ErrorBox error={data.error} /> : d && (
          <>
            <div className="kpis">
              {ORDER.map((b) => {
                const k = { late: 'late', due_soon: 'dueSoon', to_ship: 'toShip', in_transit: 'inTransit', problem: 'problem', delivered_today: 'deliveredToday' }[b] as keyof ModeStats;
                const v = sum(k);
                return (
                  <button key={b} type="button" className={`kpi${bucket === b ? ' active' : ''}${(b === 'late' || b === 'problem') && v > 0 ? ' alert' : ''}`}
                    style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => set({ bucket: bucket === b ? null : b, page: null })}>
                    <span className="k-label">{BUCKET[b].label}</span><span className="k-value">{int(v)}</span><span className="k-note">{BUCKET[b].note}</span>
                  </button>
                );
              })}
            </div>
            {one && (
              <div className="panel small" style={{ padding: '10px 14px', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                <span>Despachados no prazo: <b>{one.shippedOnTimePct == null ? '—' : pct(one.shippedOnTimePct)}</b></span>
                <span>Entregues no prazo: <b>{one.deliveredOnTimePct == null ? '—' : pct(one.deliveredOnTimePct)}</b></span>
                <span>Do pagamento ao despacho: <b>{mins(one.avgToShipMin)}</b></span>
                <span>Do despacho à entrega: <b>{mins(one.avgToDeliverMin)}</b></span>
                <span className="muted">últimos {params.get('days') ?? 7} dias · {MODE[one.mode]}</span>
              </div>
            )}

            <div className="toolbar">
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Nº do pedido, carrinho, envio ou comprador" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <select className="select" aria-label="Período" value={params.get('days') ?? '7'} onChange={(e) => set({ days: e.target.value === '7' ? null : e.target.value, page: null })}>
                {[['1', 'Últimas 24 h'], ['3', 'Últimos 3 dias'], ['7', 'Últimos 7 dias'], ['15', 'Últimos 15 dias'], ['30', 'Últimos 30 dias']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div className="panel flush">
              {!data.isPending && d.rows.length === 0 ? <div className="empty"><b>Nada por aqui</b>{bucket ? 'Nenhum envio nessa situação.' : 'Sem envios em aberto no período.'}</div> : (
                <div className="grid-wrap" style={{ opacity: data.isPlaceholderData ? 0.55 : 1 }}>
                  <table className="grid" style={{ width: '100%', minWidth: 1120 }}>
                    <thead><tr><th style={{ width: 175 }}>Prazo de despacho</th><th style={{ width: 170 }}>Pedido</th><th>Itens</th><th style={{ width: 125 }}>Modalidade</th><th style={{ width: 200 }}>Situação do envio</th><th style={{ width: 160 }}>Previsão de entrega</th><th style={{ width: 90 }} className="num">Total</th></tr></thead>
                    <tbody>
                      {d.rows.map((r) => (
                        <tr key={r.shipmentId} onClick={() => setOpen(r.orderId)} style={{ cursor: 'pointer', ...(r.mode === 'instant' ? { background: 'var(--ok-bg)' } : {}) }}>
                          <td>
                            {r.shippedAt ? <span className="small muted">despachado {dateTime(r.shippedAt)}</span> : (
                              <div className="cell-2">
                                <b style={{ color: r.bucket === 'late' ? 'var(--err)' : r.bucket === 'due_soon' ? 'var(--warn)' : undefined }}>{countdown(r.handlingLimit)}</b>
                                <span className="sub">{r.handlingLimit ? dateTime(r.handlingLimit) : 'sem prazo informado'}</span>
                              </div>
                            )}
                          </td>
                          <td><div className="cell-2"><span className="code">{r.displayId}</span><span className="sub">{r.orders > 1 ? `carrinho · ${r.orders} pedidos` : (r.buyer ?? '')}</span></div></td>
                          <td><div className="cell-2"><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.firstTitle ?? '—'}{r.items > 1 && <span className="muted"> +{r.items - 1}</span>}</span><span className="sub">{int(r.units)} un.{r.service ? ` · ${r.service}` : ''}</span></div></td>
                          <td><Tag tone={r.mode === 'instant' ? 'ok' : r.mode === 'flex' ? 'info' : 'off'}>{MODE[r.mode]}</Tag></td>
                          <td>
                            <div className="cell-2">
                              <span>{r.bucket && (r.bucket === 'late' || r.bucket === 'problem') ? <Tag tone="err">{BUCKET[r.bucket].label.replace(/s$/, '').toLowerCase()}</Tag> : null} {r.status ? STATUS[r.status] ?? r.status : '—'}</span>
                              <span className="sub">{r.substatus ? SUB[r.substatus] ?? r.substatus.replaceAll('_', ' ') : ''}{r.deliveredAt ? ` ${dateTime(r.deliveredAt)}` : ''}</span>
                            </div>
                          </td>
                          <td className="small">{r.estimatedDelivery ? dateTime(r.estimatedDelivery) : '—'}</td>
                          <td className="num">{brl(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />
            </div>
          </>
        )}
      </div>
      <OrderDialog id={open} onClose={() => setOpen(null)} />
    </>
  );
}
